// Renders a run of consecutive activity entries (tool calls, subagent
// transitions) as a single macOS-style terminal box.
//
// Live mode: typing animation runs as events arrive. Rendered with a stable
// key per run so React doesn't restart the animation when new entries are
// pushed into the array — the Terminal's own sequence context handles
// per-line completion.
//
// History mode: same shell, but lines render plain (no animation). Skipping
// the typing animation on already-completed turns avoids the disorienting
// "watch all the past tool calls retype themselves" effect on every reload.

import { ChevronDown, ChevronRight } from "lucide-react";
import { useState } from "react";

import {
  AnimatedSpan,
  Terminal,
  TypingAnimation,
} from "@/components/ui/terminal";
import { cn } from "@/lib/utils";
import type { ActivityEntry } from "@/lib/types";

export function TerminalBlock({
  entries,
  live,
}: {
  entries: ActivityEntry[];
  live: boolean;
}) {
  // The SDK's built-in AskUserQuestion tool surfaces as the interactive
  // <AskUserQuestionCard> rendered separately in TimelineView. Hide its
  // tool line here so we don't show "$ Askuserquestion · running…" right
  // above the actual question card — that double-render is what made the
  // chat look broken (terminal spinner instead of the answerable card).
  const visible = entries.filter(
    (e) => !(e.kind === "tool" && /askuserquestion/i.test(e.name || e.slug || ""))
  );
  if (visible.length === 0) return null;
  entries = visible;

  // History: skip animation entirely so a 30-tool-call past turn doesn't
  // make the user wait for the full typing replay on scroll-back.
  if (!live) {
    return (
      <Terminal sequence={false} className="max-w-full">
        <div className="flex flex-col gap-y-1">
          {entries.map((entry, i) => (
            <StaticLine key={i} entry={entry} />
          ))}
        </div>
      </Terminal>
    );
  }

  return (
    <Terminal sequence startOnView={false} className="max-w-full">
      {entries.flatMap((entry, i) => renderLiveEntry(entry, i))}
    </Terminal>
  );
}

// ─── Live (animated) ──────────────────────────────────────────────────

function renderLiveEntry(entry: ActivityEntry, idx: number): React.ReactNode[] {
  if (entry.kind === "tool") {
    const cmd = formatToolCommand(entry);
    const status = formatToolStatus(entry);
    const lines: React.ReactNode[] = [
      <TypingAnimation
        key={`${idx}-cmd`}
        duration={18}
        className="text-foreground"
      >
        {cmd}
      </TypingAnimation>,
    ];
    if (status) {
      lines.push(
        <AnimatedSpan
          key={`${idx}-status`}
          className={cn(
            "pl-3 font-mono text-xs",
            entry.result === null
              ? "text-muted-foreground italic"
              : "text-emerald-700 dark:text-emerald-400"
          )}
        >
          {status}
        </AnimatedSpan>
      );
    }
    return lines;
  }
  if (entry.kind === "subagent_start") {
    return [
      <AnimatedSpan
        key={`${idx}-sa-start`}
        className="font-mono text-xs text-muted-foreground"
      >
        {`→ subagent · ${entry.agent_type ?? "?"} starting`}
      </AnimatedSpan>,
    ];
  }
  if (entry.kind === "subagent_stop") {
    return [
      <AnimatedSpan
        key={`${idx}-sa-stop`}
        className="font-mono text-xs text-muted-foreground/70"
      >
        {`↳ subagent done · ${entry.agent_type ?? "?"}`}
      </AnimatedSpan>,
    ];
  }
  if (entry.kind === "artifact") {
    return [
      <AnimatedSpan
        key={`${idx}-art`}
        className="font-mono text-xs text-muted-foreground"
      >
        {`◆ artifact · ${entry.kind_subtype}`}
      </AnimatedSpan>,
    ];
  }
  return [];
}

// ─── Static (history) ─────────────────────────────────────────────────

function StaticLine({ entry }: { entry: ActivityEntry }) {
  const [expanded, setExpanded] = useState(false);

  if (entry.kind === "tool") {
    const cmd = formatToolCommand(entry);
    const status = formatToolStatus(entry);
    const result = entry.result;
    const hasResult =
      result !== null &&
      result !== undefined &&
      String(result).trim().length > 0;
    return (
      <div className="grid gap-y-1 text-sm">
        <button
          onClick={() => hasResult && setExpanded((v) => !v)}
          className={cn(
            "flex items-start gap-1.5 text-left",
            hasResult && "cursor-pointer hover:opacity-80"
          )}
          disabled={!hasResult}
        >
          {hasResult ? (
            expanded ? (
              <ChevronDown className="mt-1 h-3 w-3 shrink-0 text-muted-foreground" />
            ) : (
              <ChevronRight className="mt-1 h-3 w-3 shrink-0 text-muted-foreground" />
            )
          ) : (
            <span className="mt-1 inline-block h-3 w-3 shrink-0" />
          )}
          <span className="font-mono text-foreground">{cmd}</span>
        </button>
        {status && (
          <span
            className={cn(
              "pl-[18px] font-mono text-xs",
              entry.result === null
                ? "text-muted-foreground italic"
                : "text-emerald-700 dark:text-emerald-400"
            )}
          >
            {status}
          </span>
        )}
        {expanded && hasResult && (
          <pre className="ml-[18px] max-h-64 overflow-auto rounded border border-border bg-muted/30 p-2 text-[11px] leading-relaxed text-muted-foreground">
            {typeof result === "string"
              ? result
              : JSON.stringify(result, null, 2)}
          </pre>
        )}
      </div>
    );
  }
  if (entry.kind === "subagent_start") {
    return (
      <span className="font-mono text-xs text-muted-foreground">
        {`→ subagent · ${entry.agent_type ?? "?"} starting`}
      </span>
    );
  }
  if (entry.kind === "subagent_stop") {
    return (
      <span className="font-mono text-xs text-muted-foreground/70">
        {`↳ subagent done · ${entry.agent_type ?? "?"}`}
      </span>
    );
  }
  if (entry.kind === "artifact") {
    return (
      <span className="font-mono text-xs text-muted-foreground">
        {`◆ artifact · ${entry.kind_subtype}`}
      </span>
    );
  }
  return null;
}

// ─── Formatting helpers ───────────────────────────────────────────────

function formatToolCommand(
  entry: Extract<ActivityEntry, { kind: "tool" }>
): string {
  const name = entry.name || entry.slug || "tool";
  const desc = entry.description ? ` — ${entry.description}` : "";
  return `$ ${name}${desc}`;
}

function formatToolStatus(
  entry: Extract<ActivityEntry, { kind: "tool" }>
): string | null {
  if (entry.result === null) return "● running…";
  const size = entry.result_size;
  if (typeof size === "number" && size > 0) {
    return `✓ returned · ${formatBytes(size)}`;
  }
  return "✓ done";
}

function formatBytes(n: number): string {
  if (n < 1024) return `${n} B`;
  if (n < 1024 * 1024) return `${(n / 1024).toFixed(1)} KB`;
  return `${(n / (1024 * 1024)).toFixed(1)} MB`;
}
